<script lang="ts">
/**
 * One cell of a skeleton row. A plain string is shorthand for a text bar of
 * that width; the object form picks the placeholder's shape:
 *  - `bar`    — a line of text (default; the first bar sweeps inverted)
 *  - `chip`   — a rounded tag (visibility pills, status chips)
 *  - `avatar` — a 24px circle (owner cells)
 *  - `pill`   — a static right-aligned block (count badges); ignores `width`
 */
export interface GaiaSkeletonCell {
  width?: string;
  shape?: 'bar' | 'chip' | 'avatar' | 'pill';
  /** Dimmed bar — a secondary line under a primary one. */
  soft?: boolean;
}
</script>

<script setup lang="ts">
import { computed, type CSSProperties, type PropType } from 'vue';
import { gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  /** The grid template GaiaDataTable hands out through its default slot —
      skeleton rows ride the exact tracks the real rows will. */
  rowStyle: {
    type: Object as PropType<CSSProperties>,
    required: true,
  },
  /** One entry per data column, in column order. Readonly so callers can
      write the array `as const` — the literal shapes then need no imported
      type annotation. */
  cells: {
    type: Array as PropType<readonly (string | GaiaSkeletonCell)[]>,
    required: true,
  },
  rows: {
    type: Number,
    default: 6,
  },
  /** Empty leading track — set when the table has a lead column (checkbox). */
  lead: {
    type: Boolean,
    default: false,
  },
  /** Empty trailing track — on by default, matching the table's action track.
      Pass false for a table rendered with `:action-width="0"`. */
  action: {
    type: Boolean,
    default: true,
  },
  /** The host table's row height, so the card doesn't change size when the
      shimmer swaps for real rows. */
  rowHeight: {
    type: Number,
    default: 56,
  },
});

const normalized = computed<GaiaSkeletonCell[]>(() =>
  props.cells.map(cell => (typeof cell === 'string' ? { width: cell } : cell))
);

const rowBorder = gaiaNeutral.borderSoft;
const shimmerBase = gaiaNeutral.surfaceMuted;
const shimmerHighlight = gaiaNeutral.surfaceSoft;
const height = computed(() => `${props.rowHeight}px`);
</script>

<template>
  <!-- The shared loading body for GaiaDataTable callers: the toolbar and
       header stay live, the body shimmers. Naive's n-skeleton draws its own
       box model and can't be laid on a grid track, so the bars are ours. -->
  <div
    v-for="index in props.rows"
    :key="index"
    class="skeleton-row"
    :style="[props.rowStyle, { minHeight: height }]"
  >
    <div v-if="props.lead" />
    <div
      v-for="(cell, position) in normalized"
      :key="position"
      class="shimmer"
      :class="[
        cell.shape ?? 'bar',
        { first: position === 0 && (cell.shape ?? 'bar') === 'bar', soft: cell.soft },
      ]"
      :style="cell.shape === 'pill' ? undefined : { width: cell.width }"
    />
    <div v-if="props.action" />
  </div>
</template>

<style scoped>
.skeleton-row {
  display: grid;
  align-items: center;
  gap: 16px;
  padding: 0 18px;
  border-bottom: 1px solid v-bind(rowBorder);
}

/* The loading bar: a gradient swept across the cell. */
.shimmer {
  height: 11px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    v-bind(shimmerHighlight) 0%,
    v-bind(shimmerBase) 45%,
    v-bind(shimmerHighlight) 90%
  );
  background-size: 480px 100%;
  animation: gaia-table-shimmer 1.5s linear infinite;
}

/* The first text bar sweeps inverted, so neighbouring rows don't read as one
   solid band. */
.shimmer.first {
  background: linear-gradient(
    90deg,
    v-bind(shimmerBase) 0%,
    v-bind(shimmerHighlight) 45%,
    v-bind(shimmerBase) 90%
  );
  background-size: 480px 100%;
}

.shimmer.soft {
  opacity: 0.6;
}

.shimmer.chip {
  height: 18px;
  border-radius: 6px;
}

.shimmer.avatar {
  height: 24px;
  border-radius: 50%;
}

.shimmer.pill {
  width: 52px;
  height: 22px;
  margin-left: auto;
  border-radius: 8px;
  animation: none;
  background: v-bind(shimmerBase);
}

@keyframes gaia-table-shimmer {
  from {
    background-position: -480px 0;
  }
  to {
    background-position: 480px 0;
  }
}
</style>
